import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MatDialogModule,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ButtonComponent } from '@bossanova/ui-kit';
import { ModMailService } from '../../services/mod-mail.service';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface FeedbackDialogData {
  userId?: string;
}

@Component({
  selector: 'bn-feedback-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ButtonComponent,
  ],
  templateUrl: './feedback-dialog.component.html',
  styleUrl: './feedback-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackDialogComponent {
  private data = inject<FeedbackDialogData>(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);
  private modMailService = inject(ModMailService);
  private dialogRef = inject(MatDialogRef<FeedbackDialogComponent>);
  private destroyRef = inject(DestroyRef);

  isSubmitting = signal(false);
  error = signal('');
  submitted = signal(false);

  form = this.fb.nonNullable.group({
    title: [''],
    content: ['', [Validators.required, Validators.minLength(1)]],
    contactInfo: [''],
  });

  onSubmit(): void {
    console.log(this.form.getRawValue());
    if (this.form.invalid || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    this.error.set('');
    this.form.disable();

    const { title, content, contactInfo } = this.form.getRawValue();

    this.modMailService
      .sendFeedback({
        title: title || undefined,
        content,
        contactInfo: contactInfo || undefined,
        userId: this.data?.userId,
      })
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
          this.form.enable();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.submitted.set(true);
        },
        error: () => {
          this.error.set('Something went wrong. Please try again.');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
